import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Button, Container, Badge } from 'shards-react';
import {
  Table,
  THead,
  Th,
  Td,
  TableRow,
  TableHeader,
  TableWrapper,
  H3
} from '@increase/typed-components';
import PageTitle from '../../../components/PageTitle';
import Errors from '../../../components/Errors';
import CredentialMigrationService from '../../../services/CredentialMigrationService';
import { Can } from '../../../permissions/helper';
import Translator from '../../../services/Translator';
import AccountService from '../../../services/AccountService';
import CredentialService from '../../../services/CredentialService';
import { NewTabLink } from '../../../components/common/NewTabLink';

const STATUS_THEME = {
  success: 'success',
  failed: 'danger',
  pending: 'secondary',
  completed: 'success',
  partially_failed: 'warning',
  api_failed: 'danger',
  resolved: 'info'
};

const RESOLVE_CONFIRM_MESSAGE =
  'Marcar la migración como resuelta permite volver a migrar la credencial y deshabilita los ' +
  'reintentos. Hacelo solo después de revisar y corregir los pasos fallidos. ¿Continuar?';

const STEPS = [
  { key: 'apiStep', label: 'API (atómico)' },
  { key: 'scraperStep', label: 'Scraper' },
  { key: 'schedulerStep', label: 'Scheduler' },
  { key: 'reassignmentStep', label: 'Usuarios y documentos' }
];

export function MigrationStatus({ migrationId }) {
  const [migration, setMigration] = useState(null);
  const [errors, setErrors] = useState([]);

  const fetchMigration = useCallback(() => {
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .get(migrationId)
      .then((data) => {
        if (!data) {
          setErrors(['No se encontró la migración']);
          return;
        }
        setMigration(data);
      })
      .catch(() => setErrors(['No se pudo cargar la migración']));
  }, [migrationId]);

  useEffect(() => {
    fetchMigration();
  }, [fetchMigration]);

  const credentialId = migration ? migration.credentialId : null;
  const fromAccountId = migration ? migration.fromAccountId : null;
  const toAccountId = migration ? migration.toAccountId : null;
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!credentialId) return undefined;

    let cancelled = false;
    const loadNames = async () => {
      const [credentialService, accountService] = await Promise.all([
        CredentialService(),
        AccountService()
      ]);
      const [credential, fromAccount, toAccount] = await Promise.allSettled([
        credentialService.get(credentialId),
        accountService.get(fromAccountId),
        accountService.get(toAccountId)
      ]);
      if (cancelled) return;
      const valueOf = (result) => (result.status === 'fulfilled' ? result.value : null);
      setNames({
        credentialUsername: (valueOf(credential) || {}).username,
        fromAccountName: (valueOf(fromAccount) || {}).name,
        toAccountName: (valueOf(toAccount) || {}).name
      });
    };
    loadNames();
    return () => {
      cancelled = true;
    };
  }, [credentialId, fromAccountId, toAccountId]);

  const inProgress = migration && ['pending', 'partially_failed'].includes(migration.status);
  useEffect(() => {
    if (!inProgress) return undefined;
    const interval = setInterval(fetchMigration, 5000);
    return () => clearInterval(interval);
  }, [inProgress, fetchMigration]);

  const onRetry = (step) => {
    setErrors([]);
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .retry(migrationId, [step])
      .then((data) => {
        if ((data.errors || []).length > 0) {
          setErrors(data.errors.map(Translator.migrationBlocker));
          return;
        }
        fetchMigration();
      })
      .catch(() => setErrors(['No se pudo reintentar el paso']));
  };

  const onResolve = () => {
    if (!window.confirm(RESOLVE_CONFIRM_MESSAGE)) return;

    setErrors([]);
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .resolve(migrationId)
      .then((data) => {
        if ((data.errors || []).length > 0) {
          setErrors(data.errors.map(Translator.migrationBlocker));
          return;
        }
        fetchMigration();
      })
      .catch(() => setErrors(['No se pudo marcar la migración como resuelta']));
  };

  if (!migration) {
    return (
      <Container className="main-content-container px-4" fluid>
        <Errors errors={errors} />
      </Container>
    );
  }

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Estado de migración" />
      </Row>
      <Errors errors={errors} />
      <p>
        <strong>Credencial:</strong>{' '}
        <NewTabLink to={`/admin/credentials/${migration.credentialId}`}>
          {migration.credentialId}
        </NewTabLink>
        {names.credentialUsername && ` - ${names.credentialUsername}`} (
        {Translator.infoProvider(migration.infoProvider)})
      </p>
      <p>
        <strong>Cuenta origen:</strong>
        <NewTabLink to={`/admin/accounts/${migration.fromAccountId}`}>
          {migration.fromAccountId}
        </NewTabLink>
        {names.fromAccountName && ` - ${names.fromAccountName}`}
      </p>
      <p>
        <strong>Cuenta destino:</strong>
        <NewTabLink to={`/admin/accounts/${migration.toAccountId}`}>
          {migration.toAccountId}
        </NewTabLink>
        {names.toAccountName && ` - ${names.toAccountName}`}
      </p>
      <p>
        <strong>Estado general:</strong>{' '}
        <Badge pill theme={STATUS_THEME[migration.status] || 'secondary'}>
          {Translator.migrationStatus(migration.status)}
        </Badge>
      </p>
      {migration.resolvedAt && (
        <p>
          <strong>Resuelta el:</strong> {new Date(migration.resolvedAt).toLocaleString()}
        </p>
      )}
      {migration.resolvable && (
        <Can action="cred:edit:acc">
          <Button onClick={onResolve} size="sm" theme="warning">
            Marcar como resuelta
          </Button>
        </Can>
      )}
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Pasos</H3>
        </TableHeader>
        <Table hasData zebra>
          <THead>
            <tr>
              <Th>Paso</Th>
              <Th>Estado</Th>
              <Th>Error</Th>
              <Th>Acción</Th>
            </tr>
          </THead>
          <tbody>
            {STEPS.map(({ key, label }) => {
              const step = migration[key];
              const failed = step.retryable;
              const stepName = key.replace('Step', '').toLowerCase();
              return (
                <TableRow key={key}>
                  <Td>{label}</Td>
                  <Td>
                    <Badge pill theme={STATUS_THEME[step.status] || 'secondary'}>
                      {Translator.migrationStatus(step.status)}
                    </Badge>
                  </Td>
                  <Td>{step.error || '-'}</Td>
                  <Td>
                    {failed && stepName !== 'api' && (
                      <Can action="cred:edit:acc">
                        <Button onClick={() => onRetry(stepName)} size="sm">
                          Reintentar
                        </Button>
                      </Can>
                    )}
                  </Td>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
      <TableWrapper className="MarginTop20">
        <TableHeader>
          <H3>Historial de eventos</H3>
        </TableHeader>
        <Table hasData={migration.events.length > 0} noDataMessage="No hay eventos" zebra>
          <THead>
            <tr>
              <Th>Fecha</Th>
              <Th>Paso</Th>
              <Th>Estado</Th>
              <Th>Error</Th>
            </tr>
          </THead>
          <tbody>
            {migration.events.map((event, index) => (
              <TableRow key={index}>
                <Td>{new Date(event.createdAt).toLocaleString()}</Td>
                <Td>{Translator.migrationStep(event.step)}</Td>
                <Td>
                  <Badge pill theme={STATUS_THEME[event.status] || 'secondary'}>
                    {Translator.migrationStatus(event.status)}
                  </Badge>
                </Td>
                <Td>{event.errorMessage || '-'}</Td>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
}

MigrationStatus.propTypes = {
  migrationId: PropTypes.string.isRequired
};
